"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Link from "next/link";
import { useCallback, useId, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * FAQs 5013 — KUK frequently asked questions.
 *
 * Port of kuk-campus-portal `components/organisms/FAQs.tsx`: a single-open
 * accordion under a ruled heading, with a "Contact Us" CTA beneath it.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * DATA SHAPE — questions live in `faq_item` rows under `faq_category`, so the
 * widget receives `categories[].faqs[]` rather than a flat array. KUK has no
 * category UI, so every category's questions are flattened into one list in
 * category-then-priority order — the same approach FAQs 1033 takes.
 *
 * OPEN-STATE TINT — v1 tints an open row `#F4F5F7` below `tab` and leaves it
 * white above, so the accordion reads as separated cards on a phone and as plain
 * rows on a desktop. Reproduced, with both surfaces as CMS fields.
 *
 * DROPPED — v1's `custom-underline` class on the heading, a project-level rule
 * this app does not have. The rule is a real CMS-coloured border here rather
 * than importing a stylesheet for one selector.
 *
 * DROPPED — v1's commented-out mobile "Need more information?" band, which is
 * dead code in the reference and renders nowhere.
 *
 * ACCESSIBILITY — v1 uses a Radix accordion, which wires `aria-expanded` /
 * `aria-controls` for free. This is a plain button + region pair with those
 * attributes set explicitly, so keyboard and screen-reader behaviour matches
 * without pulling Radix in for one widget.
 */

/**
 * v1's `.custom-underline` — a short accent rule under the section heading, used
 * on 12 headings across the reference. Defined in ITS globals.css as a `::after`
 * with `bottom:-6px; left:0; width:33%; height:5px; background:#9f1c33`.
 *
 * Reproduced as a real element rather than a pseudo-element so the colour can
 * come from a CMS field (a pseudo-element cannot read an inline style), and so
 * the classes are ones Tailwind certainly emits — an arbitrary `after:` variant
 * would need verifying against the built CSS.
 *
 * `w-fit` on the heading is load-bearing: the rule is 33% of the HEADING, and a
 * full-width h2 would stretch it across a third of the page.
 */
const HEADING_UNDERLINE =
  "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface FAQItem {
  id?: number;
  question?: string;
  question_text_color?: string;
  answer?: string;
  answer_text_color?: string;
  priority?: number;
  is_active?: boolean;
}

interface Category {
  id?: number;
  order_index?: number;
  status?: boolean;
  is_active?: boolean;
  faqs?: FAQItem[];
}

interface Faqs5013Data {
  status?: boolean | number | string;
  categories?: Category[];
  title?: string;
  title_text_color?: string;
  /** The rule under the heading. */
  section_color?: string;
  /** Section background. */
  section_background_color?: string;
  /** Open-row surface: below `tab`, then `tab` and up. */
  background_color?: string;
  accordion_answer_surface_bg?: string;
  /** Row separator. */
  accordion_item_border_color?: string;
  /** Question / answer text. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** CTA */
  top_arrow_icon?: string;
  bottom_arrow_icon?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;
  title_1?: string;
  title_1_color?: string;
  title_2?: string;
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Faqs5013 = (props: { data?: Faqs5013Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Faqs5013Data | undefined;
  const dispatch = useDispatch();

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const accordionId = useId();

  const {
    title = "",
    title_text_color = "#031F44",
    section_color = "#9F1C33",
    section_background_color = "#FFFFFF",
    background_color = "#F4F5F7",
    accordion_answer_surface_bg = "#FFFFFF",
    accordion_item_border_color = "rgba(3, 31, 68, 0.12)",
    gradient_color_1 = "#031F44",
    gradient_color_2 = "rgba(3, 31, 68, 0.50)",
    /** CTA label / link / colours. */
    title_1: cta_label = "Contact Us",
    title_2: cta_link = "",
    gradient_color_3: cta_bg = "#9F1C33",
    gradient_color_4: cta_text_color = "#FFFFFF",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  /* Flattened in category order, then question priority — KUK ships no category
     UI, so a single list is what the reference renders. */
  const faqs = useMemo(() => {
    const cats = sectionData?.categories;
    if (!Array.isArray(cats)) return [];
    return cats
      .filter((c) => c?.status !== false && c?.is_active !== false)
      .sort((a, b) => (Number(a?.order_index) || 0) - (Number(b?.order_index) || 0))
      .flatMap((c) =>
        (Array.isArray(c.faqs) ? c.faqs : [])
          .filter((f) => f?.is_active !== false && (f?.question || f?.answer))
          .sort((a, b) => (Number(a?.priority) || 0) - (Number(b?.priority) || 0)),
      );
  }, [sectionData?.categories]);

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "faqs_5013",
      component: "faq_contact_us",
      label: cta_label?.trim() || "contact_us",
      step: "1",
      entity: "faqs_5013",
      parentEntity: "faqs_5013",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [cta_label, dispatch]);

  if (!status || faqs.length === 0) return null;

  const ctaClassName = cn(
    "inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md",
    /* v1: `px-5 py-2 h-[40px] text-md` on the Contact Us button. */
    "px-5 text-base font-light shadow transition-colors",
  );
  const ctaStyle = { backgroundColor: cta_bg, color: cta_text_color };

  return (
    <section
      className="w-full"
      style={
        {
          backgroundColor: section_background_color,
          "--kuk-faq-open": background_color,
          "--kuk-faq-open-tab": accordion_answer_surface_bg,
        } as React.CSSProperties
      }
    >
      <div className="mx-auto w-full max-w-[1120px] space-y-5 p-10 tab:px-10">
        {title ? (
          <h2
            className="relative w-fit text-2xl font-bold tab:text-4xl"
            style={{ color: title_text_color }}
          >
            {title}
            <span
              aria-hidden
              className={HEADING_UNDERLINE}
              style={{ backgroundColor: section_color }}
            />
          </h2>
        ) : null}

        <div>
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const panelId = `${accordionId}-panel-${index}`;
            const buttonId = `${accordionId}-button-${index}`;
            return (
              <div
                key={faq.id ?? index}
                className="border-b"
                style={{ borderBottomColor: accordion_item_border_color }}
              >
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className={cn(
                      "flex w-full items-center justify-between gap-4 py-4 text-left text-base tab:text-lg",
                      isOpen &&
                        "rounded-lg bg-[var(--kuk-faq-open)] px-3 tab:bg-[var(--kuk-faq-open-tab)] tab:px-0",
                    )}
                    /* v1 tints an open row below `tab` and leaves it white above.
                       Both surfaces ride in on custom properties so the one rule
                       above can carry each breakpoint's fill. */
                    style={
                      {
                        color: faq.question_text_color || gradient_color_1,
                      } as React.CSSProperties
                    }
                  >
                    <span>{faq.question}</span>
                    <span
                      aria-hidden
                      className={cn(
                        "shrink-0 transition-transform duration-200",
                        isOpen && "rotate-180",
                      )}
                    >
                      &#8964;
                    </span>
                  </button>
                </h3>
                {isOpen && faq.answer ? (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className="rounded-lg bg-[var(--kuk-faq-open)] px-3 pb-4 text-sm tab:bg-[var(--kuk-faq-open-tab)] tab:px-0"
                    style={{ color: faq.answer_text_color || gradient_color_2 }}
                  >
                    {faq.answer}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* v1 hides this below `tab` — the mobile page already ends on a CTA band. */}
        {cta_label ? (
          <div className="hidden tab:block">
            {cta_link ? (
              <Link
                href={normalizeHref(cta_link)}
                className={ctaClassName}
                style={ctaStyle}
              >
                {cta_label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleCtaClick}
                className={ctaClassName}
                style={ctaStyle}
              >
                {cta_label}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default Faqs5013;
